import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './VolumeControlsCard.module.scss';
import TimeInput from '../../../../components/TimeInput/TimeInput';
import { CustomButton } from '../../../../components/Button/Button';
import Icon from '../../../../components/Icon/Icon';
import Tooltip from '../../../../components/Tooltip/Tooltip';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import VolumeControls from '../VolumeControls/VolumeControls';

const VolumeControlsCard = ({ item, isActive, volume, onChange, music, onSelectItem, maxVideoVolume = 100 }) => {

    const { t } = useTranslation();
    const [muteHover, setMuteHover] = useState(false);
    const [tooltipTop, setTooltipTop] = useState(0);
    const [cardHover, setCardHover] = useState(false);

    const wrapperRef = useRef();

    const [expand, setExpand] = useState(false);

    const handleChangeVolume = (type) => (value) => {
        onChange(item.id, value / 100, type);
    }

    const toggleMuteVideo = useCallback(() => {
        const value = volume === 0 ? 1 : 0;
        onChange(item.id, value, 'video');
    }, [volume, item])

    const onHover = (e) => {
        const parentRect = wrapperRef.current?.parentElement?.getBoundingClientRect();
        setMuteHover(true);
        const targetRect = e.currentTarget.getBoundingClientRect();
        let position = targetRect.top + targetRect.height;
        if (e.clientY - 100 < parentRect.top) {
            position = e.clientY + e.currentTarget.offsetHeight + 10;
        }
        if (e.clientY + 200 > parentRect.top + parentRect.height) {
            position = e.clientY - 4 * e.currentTarget.offsetHeight;
        }
        setTooltipTop(position);
    }

    const volumeOptions = useMemo(() => [
        { value: volume, type: 'video' },
        ...(music ? [{ value: music.volume, type: 'music' }] : []),
    ], [volume, music])

    useEffect(() => {
        isActive &&
            wrapperRef &&
            wrapperRef.current.scrollIntoView({ behavior: 'smooth', inline: 'nearest' });
    }, [isActive]);

    return <div ref={wrapperRef} className={styles.wrapper}>
        <Tooltip classNameTooltip={styles.tooltip} moveable={false} topPosition={tooltipTop} wrapperRef={wrapperRef} isHover={muteHover} text={t(volume !== 0 && (!music || music.volume !== 0) ? 'voice_over.editor.volumes.scene.mute_both' : 'voice_over.editor.volumes.scene.unmute_both')} />
        <div className={cx(styles.accordionContainer, { [styles.open]: expand, [styles.max]: music !== null, [styles.active]: isActive, [styles.hover]: cardHover })}>
            <div className={styles.dataContainer} >
                <div className={styles.timingContainer} onClick={() => onSelectItem(item?.start + 250, item.id)} onMouseOver={() => setCardHover(true)} onMouseOut={() => setCardHover(false)}>
                    <TimeInput
                        inputClassName={styles.timeStamp}
                        value={item?.start / 1000}
                        showControls={false}
                        disabled={true}
                        variant="basic"
                    />
                    <span className={styles.timestampDivider}>-</span>
                    <TimeInput
                        inputClassName={styles.timeStamp}
                        value={(item?.start + item?.trimmedDuration) / 1000}
                        showControls={false}
                        disabled={true}
                        variant="basic"
                    />
                </div>
                {item?.name && volumeOptions.length > 1 && <div className={styles.info}>
                    <span className={styles.title}>{item.name}</span>
                    <div className={styles.btnContainer} >
                        <CustomButton variant="link" className={styles.muteAllBtn} onClick={toggleMuteVideo} onMouseOver={onHover} onMouseLeave={() => setMuteHover(false)} ariaLabel={volume === 0 ? t('accessibility.no_video_volume') : t('accessibility.video_volume')}>
                            <Icon src={volume === 0 ? 'noVideoVolume' : 'videoVolume'} className={styles.icon} verticalAlign="middle" />
                        </CustomButton>
                        <CustomButton variant="link" className={styles.expandButton} onClick={() => setExpand(prevState => !prevState)} ariaLabel={t('general.hide_password')}>
                            <Icon src={'showHideArrow'} className={cx(styles.arrowIcon, { [styles.expand]: expand })} />
                        </CustomButton>
                    </div>
                </div>}
            </div>
            {volumeOptions.map(({ value, type }) => (
                <VolumeControls key={`${item?.id}_${type}`} type={type} value={value} onChange={handleChangeVolume(type)} maxVolume={type === 'video' ? maxVideoVolume : 100} />
            ))}
        </div>
    </div>
}

export default VolumeControlsCard;